import { useState, useEffect, useCallback } from 'react';
import { useScreenState } from '@/hooks/useScreenState';
import { ScreenType } from '@/types/screen.types';
import { useThemeColors, withOpacity } from '@/styles';
import { spacing, typography, radius, opacity } from '@/styles/design-tokens';
import { formatTimestamp } from '@/utils/date';
import { logger } from '@/utils/logger';
// eslint-disable-next-line no-restricted-imports -- direct HTTP call intentional: health check must bypass the orchestrator so it still works when the processing pipeline is down
import { httpClient } from '@/utils/http-client';
import { ComponentHealthStatus } from '@/business/types/ComponentHealthStatus';

interface ComponentHealth {
  component: string;
  status: ComponentHealthStatus;
  metrics: Record<string, unknown>;
  checked_at: string;
}

interface SystemHealthResponse {
  checked_at: string;
  components: ComponentHealth[];
}

const COMPONENT_LABELS: Record<string, string> = {
  postgresql: 'PostgreSQL',
  api: 'API Server',
  sqs_main: 'SQS Main Queue',
  sqs_dlq: 'SQS Dead Letter Queue',
  s3: 'S3 Storage',
};

const STATUS_LABELS: Record<ComponentHealthStatus, string> = {
  [ComponentHealthStatus.HEALTHY]: 'Healthy',
  [ComponentHealthStatus.DEGRADED]: 'Degraded',
  [ComponentHealthStatus.UNHEALTHY]: 'Unhealthy',
  [ComponentHealthStatus.NOT_MONITORED]: 'Not Monitored',
  [ComponentHealthStatus.UNKNOWN]: 'Unknown',
};

function formatDateTime(dateStr: string): string {
  try {
    return formatTimestamp(dateStr, 'datetime') || dateStr;
  } catch {
    return dateStr;
  }
}

export default function SystemHealth() {
  const colors = useThemeColors();
  const [components, setComponents] = useState<ComponentHealth[]>([]);
  const [checkedAt, setCheckedAt] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [expandedComponent, setExpandedComponent] = useState<string | null>(
    null
  );

  const statusColors: Record<ComponentHealthStatus, string> = {
    [ComponentHealthStatus.HEALTHY]: colors.success,
    [ComponentHealthStatus.DEGRADED]: colors.warning.text,
    [ComponentHealthStatus.UNHEALTHY]: colors.error,
    [ComponentHealthStatus.NOT_MONITORED]: colors.text.tertiary,
    [ComponentHealthStatus.UNKNOWN]: colors.text.tertiary,
  };

  const loadHealth = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await httpClient.get<SystemHealthResponse>(
        '/api/v1/system/health'
      );
      setComponents(result.components ?? []);
      setCheckedAt(result.checked_at);
    } catch (error) {
      logger.error('System Health: failed to load health data', error);
      setComponents([]);
      setCheckedAt('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useScreenState({
    metadata: {
      screenType: ScreenType.SYSTEM_HEALTH,
      requiresAuth: true,
    },
    onLoad: async (_strategy, screenState) => {
      await loadHealth();
      screenState.setSuccess();
    },
  });

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadHealth();
    }, 60000);
    return () => clearInterval(interval);
  }, [loadHealth]);

  // Compute overall status (exclude not_monitored components)
  const monitoredComponents = components.filter(
    c => c.status !== ComponentHealthStatus.NOT_MONITORED
  );
  const overallStatus =
    monitoredComponents.length === 0
      ? ComponentHealthStatus.UNKNOWN
      : monitoredComponents.some(
            c => c.status === ComponentHealthStatus.UNHEALTHY
          )
        ? ComponentHealthStatus.UNHEALTHY
        : monitoredComponents.some(
              c => c.status === ComponentHealthStatus.DEGRADED
            )
          ? ComponentHealthStatus.DEGRADED
          : ComponentHealthStatus.HEALTHY;

  const renderMetrics = (metrics: Record<string, unknown>) => {
    return Object.entries(metrics).map(([key, value]) => (
      <div key={key} style={{ marginBottom: spacing.xs }}>
        <span
          style={{
            color: colors.text.secondary,
            fontWeight: typography.weights.medium,
          }}
        >
          {key}:
        </span>{' '}
        <span style={{ color: colors.text.primary }}>
          {typeof value === 'object'
            ? JSON.stringify(value, null, 2)
            : String(value)}
        </span>
      </div>
    ));
  };

  return (
    <div
      style={{
        padding: spacing.lg,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.lg,
        }}
      >
        <div>
          <h2
            style={{
              color: colors.text.primary,
              fontSize: typography.sizes['2xl'],
              fontWeight: typography.weights.semibold,
              margin: 0,
            }}
          >
            System Health
          </h2>
          <p
            style={{
              color: colors.text.secondary,
              fontSize: typography.sizes.sm,
              margin: `${spacing.xs} 0 0 0`,
            }}
          >
            {checkedAt
              ? `Last checked: ${formatDateTime(checkedAt)}`
              : 'No health data available yet'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor:
                statusColors[overallStatus] || colors.text.tertiary,
            }}
          />
          <span
            style={{
              color: colors.text.primary,
              fontWeight: typography.weights.medium,
            }}
          >
            {overallStatus === ComponentHealthStatus.UNKNOWN
              ? 'No Data'
              : STATUS_LABELS[overallStatus] || overallStatus}
          </span>
          {isLoading && (
            <span
              style={{
                color: colors.text.secondary,
                fontSize: typography.sizes.xs,
              }}
            >
              Refreshing...
            </span>
          )}
        </div>
      </div>

      {/* Component Status Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          alignItems: 'start',
          gap: spacing.md,
          marginBottom: spacing.xl,
        }}
      >
        {components.map(comp => {
          const isExpanded = expandedComponent === comp.component;

          return (
            <div
              key={comp.component}
              onClick={() =>
                setExpandedComponent(isExpanded ? null : comp.component)
              }
              style={{
                backgroundColor: colors.background,
                border: `1px solid ${colors.border.default}`,
                borderRadius: radius.lg,
                padding: spacing.lg,
                cursor: 'pointer',
                transition: 'border-color 0.2s',
                borderColor: isExpanded
                  ? statusColors[comp.status] || colors.border.default
                  : colors.border.default,
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: spacing.sm,
                }}
              >
                <span
                  style={{
                    color: colors.text.primary,
                    fontWeight: typography.weights.semibold,
                    fontSize: typography.sizes.md,
                  }}
                >
                  {COMPONENT_LABELS[comp.component] || comp.component}
                </span>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                  }}
                >
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor:
                        statusColors[comp.status] || colors.text.tertiary,
                    }}
                  />
                  <span
                    style={{
                      color: statusColors[comp.status] || colors.text.tertiary,
                      fontSize: typography.sizes.sm,
                      fontWeight: typography.weights.medium,
                    }}
                  >
                    {STATUS_LABELS[comp.status] || comp.status}
                  </span>
                </div>
              </div>

              {/* Expanded metrics */}
              {isExpanded && (
                <div
                  style={{
                    marginTop: spacing.md,
                    paddingTop: spacing.md,
                    borderTop: `1px solid ${colors.border.light}`,
                  }}
                >
                  <h4
                    style={{
                      color: colors.text.primary,
                      fontSize: typography.sizes.sm,
                      fontWeight: typography.weights.semibold,
                      marginBottom: spacing.sm,
                      marginTop: 0,
                    }}
                  >
                    Metrics
                  </h4>
                  <div
                    style={{
                      fontSize: typography.sizes.xs,
                      fontFamily: 'monospace',
                      backgroundColor: withOpacity(
                        colors.border.light,
                        opacity.light
                      ),
                      padding: spacing.sm,
                      borderRadius: radius.md,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {renderMetrics(comp.metrics)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {components.length === 0 && !isLoading && (
        <div
          style={{
            textAlign: 'center',
            padding: `${spacing.xl} 0`,
            color: colors.text.secondary,
          }}
        >
          <p
            style={{ fontSize: typography.sizes.lg, marginBottom: spacing.sm }}
          >
            No health data available
          </p>
          <p style={{ fontSize: typography.sizes.sm }}>
            Unable to reach health endpoints.
          </p>
        </div>
      )}
    </div>
  );
}
